import React from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import { formatVnDate } from "@apv-erp/status";
import { trpcClient } from "@/lib/trpc";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { EntityLink } from "@/components/shared/EntityLink";
import { useConfigLookup } from "@/hooks/useConfigLookup";
import { DetailField as F } from "@/components/shared/DetailField";

/** Ô thông tin chung — cùng kiểu trang chi tiết Đơn hàng / Phiếu kho */

export default function MachineLogDetailPage() {
  const navigate = useNavigate();
  const { logId } = useParams({ strict: false }) as { logId: string };

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["machine-logs", logId],
    queryFn: async () => {
      return (await trpcClient.machineLogs.getById.query({ id: logId })) as any;
    },
  });

  const log = response;
  // Ca và loại sự cố lưu MÃ cấu hình (shifts / downtime_reasons) → hiển thị tên
  const { options: shiftOptions } = useConfigLookup("shifts");
  const { options: reasonOptions } = useConfigLookup("downtime_reasons");
  const labelOf = (opts: any[], code?: string | null) =>
    (code && opts.find((o: any) => o.id === code)?.label) || code || "-";

  const breadcrumbs = [{ label: "Nhật ký máy", to: "/machine-logs" }];
  if (isLoading) return <DetailLoadingSkeleton breadcrumbs={breadcrumbs} />;
  if (error || !log) {
    return (
      <StandardDetailLayout breadcrumbs={breadcrumbs} title="Không tìm thấy" showContextPanel={false}>
        <div className="p-8 text-center text-slate-500">Không tìm thấy nhật ký máy hoặc nhật ký đã bị xoá.</div>
      </StandardDetailLayout>
    );
  }

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "OPERATION":
        return (
          <Badge className="bg-emerald-100 text-emerald-800 border-none">
            Vận hành
          </Badge>
        );
      case "DOWNTIME":
        return (
          <Badge className="bg-rose-100 text-rose-800 border-none">
            Dừng máy
          </Badge>
        );
      case "MAINTENANCE":
        return (
          <Badge className="bg-blue-100 text-blue-800 border-none">
            Bảo trì
          </Badge>
        );
      case "SETUP":
        return <Badge className="bg-amber-100 text-amber-800 border-none">Căn chỉnh</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const isIssue = log.type === "DOWNTIME" || log.type === "MAINTENANCE";
  const operatorName = typeof log.operator === "object" && log.operator !== null ? (log.operator as any).fullName : log.operator;

  return (
    <StandardDetailLayout
      breadcrumbs={breadcrumbs}
      title={log.logId || logId}
      status={getTypeBadge(log.type)}
      entityId={log.id}
      entityType="machine_log"
      timestamps={{ created: log.createdAt, updated: log.updatedAt, createdBy: operatorName || null }}
      onBack={() => navigate({ to: "/machine-logs" })}
    >
      {/* ═══ THÔNG TIN CHUNG (dày, toàn chiều rộng — như Đơn hàng / Phiếu kho) ═══ */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-3" data-testid="machine-log-info-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5">
          <F label="Mã nhật ký" value={log.logId} mono hl />
          <F label="Ngày" value={log.date ? formatVnDate(log.date) : null} />
          <F label="Ca làm việc" value={<span data-testid="machine-log-view-shift">{labelOf(shiftOptions, log.shift)}</span>} />
          <F label="Thời lượng" value={log.durationMinutes ? `${log.durationMinutes} phút` : null} />
          <F
            label="Máy"
            value={
              log.machineId ? (
                <EntityLink
                  entityType="machine"
                  id={log.machineId}
                  label={log.machine?.name || log.machine?.machineId || log.machineId}
                  search={{ returnUrl: `/machine-logs/${logId}` }}
                />
              ) : null
            }
          />
          <F
            label="Lệnh SX"
            value={
              <span data-testid="machine-log-view-po">
                {log.productionOrderId ? (
                  <EntityLink
                    entityType="productionOrder"
                    id={log.productionOrderId}
                    code={log.productionOrder?.orderId || log.productionOrderId}
                    search={{ returnUrl: `/machine-logs/${logId}` }}
                  />
                ) : (
                  "—"
                )}
              </span>
            }
          />
          <F label="Người vận hành" value={operatorName} />
        </div>

        {isIssue && (
          <div className="mt-1.5 pt-1.5 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5">
            <F label="Loại lỗi" value={<span data-testid="machine-log-view-issue-type">{labelOf(reasonOptions, log.issueType)}</span>} />
            <div className="lg:col-span-3">
              <F label="Nguyên nhân" value={<span data-testid="machine-log-view-reason" className="whitespace-pre-wrap">{log.reason || "—"}</span>} />
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <F
                label="Biện pháp khắc phục"
                value={<span data-testid="machine-log-view-fix-action" className="whitespace-pre-wrap">{log.fixAction || "—"}</span>}
              />
            </div>
          </div>
        )}

        {log.parameters && typeof log.parameters === "object" && Object.keys(log.parameters).length > 0 && (
          <div className="mt-1.5 pt-1.5 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5">
            {Object.entries(log.parameters).map(([key, val]) => (
              <F key={key} label={key} value={typeof val === "object" ? JSON.stringify(val) : String(val)} mono />
            ))}
          </div>
        )}
      </div>
    </StandardDetailLayout>
  );
}
